import { api } from '@/api'
import type { UploadOptions } from '@/api/documents'
import type {
  AccessRequest,
  ControlReview,
  CorporateAuditEntry,
  CorporateDocument,
  CorporateDocumentCategory,
  CorporateExport,
  Corporation,
  CorporationInput,
  DateOnly,
  DirectorEntry,
  DirectorInput,
  Holding,
  Id,
  Minute,
  MinuteAdoptInput,
  MinuteBookExportInput,
  MinuteInput,
  MinuteListParams,
  MinuteTemplate,
  Obligation,
  ObligationInput,
  ObligationRule,
  ObligationStatus,
  ObligationUpdate,
  OfficerEntry,
  OfficerInput,
  Page,
  PaginationParams,
  ShareMovement,
  ShareMovementInput,
  SignificantControlEntry,
  SignificantControlInput,
} from '@/api/types'

const ref = (value: string) => encodeURIComponent(value)

/**
 * Le livre des minutes (lot 45, contrat 1.88.0) : une section privée, réservée
 * à ses **gardiens** (JWT). Pour tout autre compte, chaque route répond 404,
 * comme une route qui n'existe pas. Un seul cache, à part de tout le reste.
 */
export const CORPORATE_KEY = ['corporate'] as const

export interface CorporateDocumentUpload {
  file: File
  category: CorporateDocumentCategory
  title: string
  effective_on?: DateOnly | null
  filed_on?: DateOnly | null
  replaces_id?: Id | null
}

export const corporateApi = {
  /** `null` : livre pas encore ouvert (administrateur) ; 404 : pas gardien. */
  corporation: () => api.get<Corporation | null>('/corporate'),
  /** Administrateur, JWT ; une seule société au lot 45 (409 `corporation_exists`). */
  open: (payload: CorporationInput) => api.post<Corporation>('/corporate', payload),
  update: (payload: Partial<CorporationInput>) => api.patch<Corporation>('/corporate', payload),

  directors: (asOf?: DateOnly) =>
    api.get<DirectorEntry[]>('/corporate/directors', { query: { as_of: asOf } }),
  createDirector: (payload: DirectorInput) =>
    api.post<DirectorEntry>('/corporate/directors', payload),
  /** 409 `entry_closed` si l'écriture est figée. */
  updateDirector: (id: Id, payload: Partial<DirectorInput>) =>
    api.patch<DirectorEntry>(`/corporate/directors/${ref(id)}`, payload),
  officers: (asOf?: DateOnly) =>
    api.get<OfficerEntry[]>('/corporate/officers', { query: { as_of: asOf } }),
  createOfficer: (payload: OfficerInput) => api.post<OfficerEntry>('/corporate/officers', payload),
  updateOfficer: (id: Id, payload: Partial<OfficerInput>) =>
    api.patch<OfficerEntry>(`/corporate/officers/${ref(id)}`, payload),

  movements: () => api.get<ShareMovement[]>('/corporate/share-movements'),
  /** 422 `holding_negative` : le mouvement rendrait une détention négative. */
  createMovement: (payload: ShareMovementInput) =>
    api.post<ShareMovement>('/corporate/share-movements', payload),
  holdings: (asOf?: DateOnly) =>
    api.get<Holding[]>('/corporate/holdings', { query: { as_of: asOf } }),

  /** Entrées **masquées** : date de naissance et adresse nulles. */
  controlEntries: (asOf?: DateOnly) =>
    api.get<SignificantControlEntry[]>('/corporate/significant-control', {
      query: { as_of: asOf },
    }),
  /** L'entrée complète : journalisée côté serveur, jamais mise en cache ici. */
  revealControlEntry: (id: Id) =>
    api.post<SignificantControlEntry>(`/corporate/significant-control/${ref(id)}/reveal`),
  createControlEntry: (payload: SignificantControlInput) =>
    api.post<SignificantControlEntry>('/corporate/significant-control', payload),
  updateControlEntry: (id: Id, payload: Partial<SignificantControlInput>) =>
    api.patch<SignificantControlEntry>(`/corporate/significant-control/${ref(id)}`, payload),
  /** Les validations annuelles, de la plus récente à la plus ancienne. */
  controlReviews: () => api.get<ControlReview[]>('/corporate/significant-control/reviews'),
  /** La validation annuelle, en un geste. */
  reviewControlRegister: (payload: { outcome: 'confirmed' | 'updated'; note?: string | null }) =>
    api.post<ControlReview>('/corporate/significant-control/reviews', payload),

  accessRequests: () => api.get<AccessRequest[]>('/corporate/access-requests'),
  createAccessRequest: (payload: Omit<AccessRequest, 'id'>) =>
    api.post<AccessRequest>('/corporate/access-requests', payload),
  updateAccessRequest: (id: Id, payload: Partial<Omit<AccessRequest, 'id'>>) =>
    api.patch<AccessRequest>(`/corporate/access-requests/${ref(id)}`, payload),

  minutes: (params: MinuteListParams = {}) =>
    api.get<Minute[]>('/corporate/minutes', { query: { ...params } }),
  createMinute: (payload: MinuteInput) => api.post<Minute>('/corporate/minutes', payload),
  /** Brouillon seulement (409 `minute_adopted`). */
  updateMinute: (id: Id, payload: Partial<MinuteInput>) =>
    api.patch<Minute>(`/corporate/minutes/${ref(id)}`, payload),
  /**
   * Fige, numérote, produit le PDF et applique les effets, tout ou rien.
   * 422 `signatories_incomplete`, `scan_required`.
   */
  adoptMinute: (id: Id, payload: MinuteAdoptInput) =>
    api.post<Minute>(`/corporate/minutes/${ref(id)}/adopt`, payload),
  /** Un brouillon qui la remplace. */
  supersedeMinute: (id: Id, reason: string) =>
    api.post<Minute>(`/corporate/minutes/${ref(id)}/supersede`, { reason }),
  minutePdf: (id: Id) => api.getBlob(`/corporate/minutes/${ref(id)}/pdf`),
  templates: () => api.get<MinuteTemplate[]>('/corporate/templates'),

  documents: () => api.get<CorporateDocument[]>('/corporate/documents'),
  /** Jamais supprimée : une nouvelle version passe par `replaces_id`. */
  uploadDocument: (upload: CorporateDocumentUpload, options: UploadOptions = {}) => {
    const form = new FormData()
    form.append('file', upload.file)
    form.append('category', upload.category)
    form.append('title', upload.title)
    if (upload.effective_on) form.append('effective_on', upload.effective_on)
    if (upload.filed_on) form.append('filed_on', upload.filed_on)
    if (upload.replaces_id) form.append('replaces_id', upload.replaces_id)
    // #399 : le menu lit `GET /corporate` au démarrage ; le dépôt, lui, charge
    // le client de dépôt à la demande, hors de l'entrée.
    return import('@/api/documents').then(({ uploadForm }) =>
      uploadForm<CorporateDocument>('/corporate/documents', form, options),
    )
  },
  documentContent: (id: Id) => api.getBlob(`/corporate/documents/${ref(id)}/content`),

  obligations: (params: { from?: DateOnly; to?: DateOnly; status?: ObligationStatus[] } = {}) =>
    api.get<Obligation[]>('/corporate/obligations', {
      query: { ...params, status: params.status?.join(',') },
    }),
  createObligation: (payload: ObligationInput) =>
    api.post<Obligation>('/corporate/obligations', payload),
  /** `done` avec sa preuve ; `dismissed` exige une note (422 `note_required`). */
  updateObligation: (id: Id, payload: ObligationUpdate) =>
    api.patch<Obligation>(`/corporate/obligations/${ref(id)}`, payload),
  rules: () => api.get<ObligationRule[]>('/corporate/obligation-rules'),
  saveRules: (rules: ObligationRule[]) =>
    api.put<ObligationRule[]>('/corporate/obligation-rules', rules),

  /** 202 : l'export se prépare ; on sonde `exportStatus` jusqu'à `ready`. */
  startExport: (payload: MinuteBookExportInput) =>
    api.post<CorporateExport>('/corporate/export', payload),
  exportStatus: (id: Id) => api.get<CorporateExport>(`/corporate/export/${ref(id)}`),
  exportPdf: (id: Id) => api.getBlob(`/corporate/export/${ref(id)}/pdf`),
  exportZip: (id: Id) => api.getBlob(`/corporate/export/${ref(id)}/zip`),

  audit: (params: PaginationParams = {}) =>
    api.get<Page<CorporateAuditEntry>>('/corporate/audit', { query: { ...params } }),
}
